"use client";

import { useCallback, useEffect, useId, useState } from "react";
import type { DragEvent } from "react";
import * as s from "@/lib/styles";
import { Modal } from "@/components/modal";
import { RequireAuth } from "@/components/require-auth";
import { useToast } from "@/components/toast-provider";
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api";
import { EditIcon, TrashIcon } from "@/lib/icons";

interface LinkItem {
  id: number;
  groupId: number;
  label: string;
  url: string;
  login: string;
  description: string;
  position: number;
}

interface Group {
  id: number;
  title: string;
  description: string;
  position: number;
  links: LinkItem[];
}

const dangerBtn =
  "text-xs px-2 py-1 rounded border border-border text-muted hover:text-red hover:border-red transition-colors";

// ---- Link editing ----

function LinkEditor({
  initial,
  groupId,
  onSaved,
  onCancel,
}: {
  initial?: LinkItem;
  groupId: number;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { showToast } = useToast();
  const uid = useId();
  const [label, setLabel] = useState(initial?.label ?? "");
  const [url, setUrl] = useState(initial?.url ?? "");
  const [login, setLogin] = useState(initial?.login ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!label.trim() || !url.trim()) {
      showToast("error", "Label and URL are required");
      return;
    }
    setBusy(true);
    try {
      if (initial) {
        await apiPut(`/dashboard/links/${initial.id}`, { label, url, login, description });
        showToast("success", "Link updated");
      } else {
        await apiPost("/dashboard/links", { groupId, label, url, login, description });
        showToast("success", "Link added");
      }
      onSaved();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Failed to save link");
    } finally {
      setBusy(false);
    }
  };

  const field = (key: string, labelText: string, value: string, set: (v: string) => void, placeholder: string, autoFocus = false) => (
    <div className={s.fieldRow}>
      <label htmlFor={`${uid}-${key}`} className={s.label}>{labelText}</label>
      <input
        id={`${uid}-${key}`}
        className={s.input}
        placeholder={placeholder}
        value={value}
        onChange={(e) => set(e.target.value)}
        autoFocus={autoFocus}
        onKeyDown={(e) => { if (e.key === "Enter") save(); }}
      />
    </div>
  );

  return (
    <Modal onClose={onCancel} dismissable={!busy}>
      <h2 className="text-lg font-semibold text-purple">{initial ? "Edit link" : "Add link"}</h2>
      <div className="space-y-3">
        {field("label", "Label", label, setLabel, "e.g. GitHub", true)}
        {field("url", "URL", url, setUrl, "https://… or /path")}
        {field("login", "Login", login, setLogin, "username (optional)")}
        {field("description", "Description", description, setDescription, "optional")}
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className={s.btnAuxiliary} onClick={onCancel} disabled={busy}>Cancel</button>
        <button className={s.btnPrimary} onClick={save} disabled={busy}>Save</button>
      </div>
    </Modal>
  );
}

interface Dnd {
  draggable: boolean;
  isDragging: boolean;
  isOver: boolean;
  onHandleDown: () => void;
  onHandleUp: () => void;
  onDragStart: () => void;
  onDragOver: (e: DragEvent<HTMLTableRowElement>) => void;
  onDrop: () => void;
  onDragEnd: () => void;
}

function LinkRow({ link, editMode, onChanged, dnd }: { link: LinkItem; editMode: boolean; onChanged: () => void; dnd: Dnd }) {
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);

  const remove = async () => {
    if (!window.confirm(`Delete link “${link.label}”?`)) return;
    try {
      await apiDelete(`/dashboard/links/${link.id}`);
      showToast("success", "Link deleted");
      onChanged();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Failed to delete link");
    }
  };

  return (
    <tr
      draggable={dnd.draggable}
      onDragStart={dnd.onDragStart}
      onDragOver={dnd.onDragOver}
      onDrop={(e) => { e.preventDefault(); dnd.onDrop(); }}
      onDragEnd={dnd.onDragEnd}
      className={`align-baseline odd:bg-surface even:bg-surface-light ${dnd.isDragging ? "opacity-40" : ""} ${dnd.isOver ? "border-t-2 border-accent" : ""}`}
    >
      {/* drag handle — grab to reorder within the card */}
      <td className="py-0.5 pl-1 pr-2 w-4 whitespace-nowrap align-middle">
        <span
          onMouseDown={dnd.onHandleDown}
          onMouseUp={dnd.onHandleUp}
          className="cursor-grab active:cursor-grabbing text-muted hover:text-foreground select-none inline-flex"
          title="Drag to reorder"
          aria-label="Drag to reorder"
          role="button"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </span>
      </td>
      {/* link-text column — sizes to the widest label */}
      <td className="py-0.5 pl-1 pr-6 whitespace-nowrap">
        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
          {link.label}
        </a>
      </td>
      {/* login column — sizes to the widest login, sits before the description */}
      <td className="py-0.5 pr-6 whitespace-nowrap text-xs text-muted">{link.login}</td>
      {/* description column — sizes to the widest description */}
      <td className="py-0.5 pr-2 whitespace-nowrap text-xs text-muted">{link.description}</td>
      {/* per-link actions: pencil to edit (always), Delete in edit mode */}
      <td className="py-0.5 pl-2 pr-2.5 whitespace-nowrap text-right align-middle">
        <span className="inline-flex items-center gap-2">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center justify-center shrink-0 hover:opacity-80 transition-opacity"
            title="Edit link"
            aria-label="Edit link"
          >
            <EditIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={remove}
            className="inline-flex items-center justify-center shrink-0 hover:opacity-80 transition-opacity"
            title="Delete link"
            aria-label="Delete link"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </span>
        {editing && (
          <LinkEditor
            initial={link}
            groupId={link.groupId}
            onSaved={() => { setEditing(false); onChanged(); }}
            onCancel={() => setEditing(false)}
          />
        )}
      </td>
    </tr>
  );
}

// ---- Group editing ----

function GroupCard({ group, editMode, onChanged }: { group: Group; editMode: boolean; onChanged: () => void }) {
  const { showToast } = useToast();
  const [editingGroup, setEditingGroup] = useState(false);
  const [addingLink, setAddingLink] = useState(false);
  const [title, setTitle] = useState(group.title);
  const [description, setDescription] = useState(group.description);
  const [busy, setBusy] = useState(false);

  // --- drag-to-reorder state. `order` mirrors group.links so the row can move
  // under the cursor before the server round-trip lands.
  const [order, setOrder] = useState<LinkItem[]>(group.links);
  useEffect(() => { setOrder(group.links); }, [group.links]);
  const [grabbedId, setGrabbedId] = useState<number | null>(null);
  const [fromIdx, setFromIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  const persistOrder = async (next: LinkItem[]) => {
    const prev = order;
    setOrder(next);
    try {
      await apiPost("/dashboard/links/reorder", { groupId: group.id, ids: next.map((l) => l.id) });
      onChanged();
    } catch (e) {
      setOrder(prev);
      showToast("error", e instanceof Error ? e.message : "Failed to save order");
    }
  };

  const dropAt = (index: number) => {
    if (fromIdx === null || fromIdx === index) return;
    const next = [...order];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(index, 0, moved);
    void persistOrder(next);
  };

  const saveGroup = async () => {
    if (!title.trim()) {
      showToast("error", "Title is required");
      return;
    }
    setBusy(true);
    try {
      await apiPut(`/dashboard/groups/${group.id}`, { title, description });
      showToast("success", "Group updated");
      setEditingGroup(false);
      onChanged();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Failed to update group");
    } finally {
      setBusy(false);
    }
  };

  // An empty section is cheap to delete, so it gets an always-visible trash can
  // next to the pencil; a section holding links keeps the edit-mode-only button.
  const isEmpty = group.links.length === 0;

  const removeGroup = async () => {
    const prompt = isEmpty
      ? `Delete group “${group.title}”?`
      : `Delete group “${group.title}” and all its links?`;
    if (!window.confirm(prompt)) return;
    try {
      await apiDelete(`/dashboard/groups/${group.id}`);
      showToast("success", "Group deleted");
      onChanged();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Failed to delete group");
    }
  };

  return (
    <div className="space-y-1 w-fit">
      {/* Label header — pencil sits on the underline, level with the label.
          Underline (border-b) runs the full width of the block. */}
      <div className="px-1 pb-1 border-b border-border">
        <div className="flex items-center justify-between gap-6">
          <h2 className="text-lg font-semibold text-purple break-words">{group.title}</h2>
          <div className="flex items-center gap-2 shrink-0">
            {isEmpty ? (
              <button
                type="button"
                onClick={removeGroup}
                className="inline-flex items-center justify-center shrink-0 hover:opacity-80 transition-opacity"
                title="Delete group"
                aria-label="Delete group"
              >
                <TrashIcon className="w-[18px] h-[18px]" />
              </button>
            ) : (
              editMode && <button className={dangerBtn} onClick={removeGroup}>Delete</button>
            )}
            <button
              type="button"
              onClick={() => setEditingGroup(true)}
              className="inline-flex items-center justify-center shrink-0 hover:opacity-80 transition-opacity"
              title="Edit label"
              aria-label="Edit label"
            >
              <EditIcon className="w-[18px] h-[18px]" />
            </button>
            <button
              type="button"
              onClick={() => setAddingLink(true)}
              className="inline-flex items-center justify-center shrink-0 text-green hover:text-green-hover transition-colors"
              title="Add link"
              aria-label="Add link"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
            </button>
          </div>
        </div>
        {group.description && <p className="text-sm text-muted break-words mt-0.5">{group.description}</p>}
      </div>

      {/* Links as a table — link-text and description columns each size to their widest cell */}
      <table className="border-collapse">
        <tbody>
          {order.length === 0 && !editMode && (
            <tr><td className="text-sm text-muted">No links yet.</td></tr>
          )}
          {order.map((l, i) => (
            <LinkRow
              key={l.id}
              link={l}
              editMode={editMode}
              onChanged={onChanged}
              dnd={{
                draggable: grabbedId === l.id,
                isDragging: fromIdx === i,
                isOver: overIdx === i && fromIdx !== i,
                onHandleDown: () => setGrabbedId(l.id),
                onHandleUp: () => setGrabbedId(null),
                onDragStart: () => setFromIdx(i),
                onDragOver: (e) => { e.preventDefault(); setOverIdx(i); },
                onDrop: () => dropAt(i),
                onDragEnd: () => { setGrabbedId(null); setFromIdx(null); setOverIdx(null); },
              }}
            />
          ))}
        </tbody>
      </table>

      {addingLink && (
        <LinkEditor groupId={group.id} onSaved={() => { setAddingLink(false); onChanged(); }} onCancel={() => setAddingLink(false)} />
      )}

      {/* Edit-label modal */}
      {editingGroup && (
        <Modal
          dismissable={!busy}
          onClose={() => { setEditingGroup(false); setTitle(group.title); setDescription(group.description); }}
        >
          <h2 className="text-lg font-semibold text-purple">Edit label</h2>
          <input className={s.input} placeholder="Group title" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          <input className={s.input} placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
          <div className="flex justify-end gap-2">
            <button className={s.btnAuxiliary} onClick={() => { setEditingGroup(false); setTitle(group.title); setDescription(group.description); }} disabled={busy}>Cancel</button>
            <button className={s.btnPrimary} onClick={saveGroup} disabled={busy}>Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function AddGroupForm({ onChanged }: { onChanged: () => void }) {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!title.trim()) {
      showToast("error", "Title is required");
      return;
    }
    setBusy(true);
    try {
      await apiPost("/dashboard/groups", { title, description });
      showToast("success", "Group created");
      setTitle("");
      setDescription("");
      setOpen(false);
      onChanged();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Failed to create group");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return <button className={s.btnPrimary} onClick={() => setOpen(true)}>+ New group</button>;
  }
  return (
    <div className={`${s.card} max-w-md`}>
      <input className={s.input} placeholder="Group title" value={title} onChange={(e) => setTitle(e.target.value)} />
      <input className={s.input} placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
      <div className="flex gap-2">
        <button className={s.btnPrimary} onClick={create} disabled={busy}>Create</button>
        <button className={s.btnAuxiliary} onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
      </div>
    </div>
  );
}

// Split the groups into two side-by-side columns, each a stack of its own.
// Groups keep their order reading down the left column then down the right; the
// cut point balances the two stacks by rough rendered height (label + one row
// per link) so neither column trails a long run of empty space.
function splitColumns(groups: Group[]): [Group[], Group[]] {
  const height = (g: Group) => 2 + Math.max(g.links.length, 1);
  const total = groups.reduce((n, g) => n + height(g), 0);
  const left: Group[] = [];
  const right: Group[] = [];
  let used = 0;
  for (const g of groups) {
    const h = height(g);
    // straddle the midpoint rather than stopping short of it, and never go back
    // to the left column once the cut is made
    if (right.length === 0 && used + h / 2 <= total / 2) {
      left.push(g);
      used += h;
    } else {
      right.push(g);
    }
  }
  return [left, right];
}

function DashboardInner() {
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);

  const load = useCallback(() => {
    apiGet<Group[]>("/dashboard/groups")
      .then((g) => { setGroups(g); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load dashboard"));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="w-[95%] mx-auto pt-2 pb-8 px-4">
      <div className="flex justify-end mb-2">
        <button className={editMode ? s.btnPrimary : s.btnAuxiliary} onClick={() => setEditMode((v) => !v)}>
          {editMode ? "Done" : "Edit"}
        </button>
      </div>

      {error && <p className="text-red mb-4">{error}</p>}
      {!groups && !error && <p className="text-muted">Loading…</p>}

      {groups && groups.length === 0 && !editMode && (
        <p className="text-muted">No groups yet. Click <span className="text-foreground">Edit</span> to create your first one.</p>
      )}

      {groups && (
        <div className="flex flex-col md:flex-row md:items-start gap-x-10 gap-y-6">
          {splitColumns(groups).map((col, i) => (
            col.length > 0 && (
              <div key={i} className="flex-1 md:basis-0 flex flex-col gap-6 items-start">
                {col.map((g) => (
                  <GroupCard key={g.id} group={g} editMode={editMode} onChanged={load} />
                ))}
              </div>
            )
          ))}
        </div>
      )}

      {editMode && (
        <div className="mt-8">
          <AddGroupForm onChanged={load} />
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  return (
    <RequireAuth>
      <DashboardInner />
    </RequireAuth>
  );
}
